import {
    DocumentType,
    modelOptions,
    PropType,
    plugin,
    post,
    pre,
    prop,
    type Ref,
    Severity,
} from "@typegoose/typegoose";
import mongoose from "mongoose";
import mongooseAutoPopulate from "mongoose-autopopulate";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import { DeviceTypeConfigurations } from "../../Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { ThirdPartyDeviceType } from "../../DeviceTypes/ThirdPartyDevice.js";
import { RootLogger } from "../../logging/index.js";
import type { DocumentReference } from "../../shared.js";
import { ONLINE_STATUS } from "../../shared.js";
import { PinoModules } from "../../utils/index.js";
import { toLoraEui } from "../../utils/shortCodes/EuiShortCodeMapper.js";
import { TimeOverride } from "../../utils/timeOverrideUtils.js";
import { loraEuiRegex } from "../assetIdentity/assetIdentity.js";
import {
    getZodSchemaFromClass,
    ObjectIdString,
    ThirdPartyDeviceTypeString,
    validate,
} from "../../zod-decorator/index.js";
import { assetIdentitySync } from "../assetIdentity/assetIdentitySyncHook.js";
import { ExternalLink } from "../commonSubSchemas/externalLink.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { SensorSpecifier } from "../device/subSchemas/sensorSpecifier.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { ThirdPartyDeviceFieldsWithDefaults, ThirdPartyDeviceSchemaTypes } from "../types/thirdPartyDevice.d.js";
import { createDelayedRegistrationHook } from "../utils/delayedRegistrationHook.js";
import { trackMixed } from "../utils/mixedSchemaSavingUtils.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { Warning } from "../warning/warning.js";
import * as getters from "./getters.js";

const SCHEMA_KEY = "ThirdPartyDevice" as const;

/** MongoDB collection name for {@link ThirdPartyDevice} documents. */
export const THIRD_PARTY_DEVICE_COLLECTION_NAME = "thirdPartyDevices" as const;

const logger = RootLogger.child({ module: PinoModules.models.assetIdentity });

/**
 * Generates and assigns a unique verdiId to the third-party device. Registered in the backend
 * (where AssetIdentityModel lives) because verdi-common cannot import backend code.
 */
export const thirdPartyDeviceVerdiIdGenerationPreValidate = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => Promise<void>
>("thirdPartyDeviceVerdiIdGenerationPreValidate");

export const generatedSensorClusterSyncPreSave = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => void
>("thirdPartyDeviceGeneratedSensorClusterSyncPreSave", { synchronous: true });

export const generatedSensorClusterSyncPostSave = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => Promise<void>
>("thirdPartyDeviceGeneratedSensorClusterSyncPostSave");

// Generates verdiId on "validate" rather than "save" since verdiId is a required field
@pre<ThirdPartyDevice>("validate", async function (this: DocumentType<ThirdPartyDevice>) {
    if (this.verdiId) return;
    try {
        await thirdPartyDeviceVerdiIdGenerationPreValidate.function(this);
    } catch (e) {
        logger.error({ err: e, thirdPartyDeviceId: this._id }, "could not generate verdiId for third-party device");
        throw e;
    }
})
@pre<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    await generatedSensorClusterSyncPreSave.function(this);
})
@post<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    if (!this.verdiId) return;
    // TODO: Once AssetIdentity becomes the source of truth for asset IDs, this should hard-fail instead of
    // catching. We swallow errors here only because the transition is incomplete — device saves must not
    // break if the sync fails during this interim period.
    await assetIdentitySync
        .function({
            assetCollection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
            assetType: AssetType.THIRD_PARTY_DEVICE,
            loraEui: this.node_id ? toLoraEui(this.node_id) : undefined,
            verdiId: this.verdiId,
        })
        .catch((e) =>
            logger.warn({ err: e, verdiId: this.verdiId }, "could not sync asset identity for third-party device"),
        );
})
@post<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    await generatedSensorClusterSyncPostSave.function(this);
})
@modelOptions({
    schemaOptions: {
        collection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@plugin(mongooseAutoPopulate as any)
@post<SchemaBase<ThirdPartyDevice>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ThirdPartyDevice extends SchemaBase<ThirdPartyDevice, ThirdPartyDeviceFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({
        childClass: VertexInfo,
    })
    @prop({ type: () => VertexInfo, required: true, _id: false })
    public position!: VertexInfo;

    // The asset identity sync derives loraEui from this, and AssetIdentity rejects anything else.
    @validate({ zodValidator: z.string().toLowerCase().regex(loraEuiRegex) })
    @prop({ type: () => String, required: true, unique: true, match: loraEuiRegex })
    public node_id!: string;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public name!: string;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    // TODO: Update to actually reflect being Zone References
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ type: () => [mongoose.Types.ObjectId], required: true, default: [] }, PropType.ARRAY)
    public connectedZones: DocumentReference[] = [];

    @validate({
        childClass: SensorSpecifier,
        childClassTransform: (object) => z.array(object).optional(),
    })
    @prop({ type: () => [SensorSpecifier], required: true, default: [], _id: false }, PropType.ARRAY)
    public connectedSensors: SensorSpecifier[] = [];

    @validate({ zodValidator: ObjectIdString.optional(), responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "AreaOfInterest" })
    public _aoi?: DocumentReference;

    @validate({ zodValidator: z.string(), responseOnly: true })
    @prop({ type: () => String, required: true, unique: true })
    public verdiId!: string;

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: "" })
    public adminNotes: string = "";

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: "" })
    public notes: string = "";

    @validate({
        childClass: ExternalLink,
        childClassTransform: (object) => z.record(z.string(), object).optional(),
    })
    @prop({ type: () => Object, required: true, default: {}, allowMixed: Severity.ALLOW })
    public externalLinks: Record<string, ExternalLink> = {};

    @validate({ childClass: Warning, childClassTransform: (object) => z.array(object).optional(), responseOnly: true })
    @prop({
        ref: () => Warning,
        localField: "node_id",
        foreignField: "foreignLookup.targetId",
        match: {
            "foreignLookup.targetCollection": THIRD_PARTY_DEVICE_COLLECTION_NAME,
            versionIsCurrent: true,
            deleted: false,
        },
        autopopulate: true,
    })
    public warnings: Warning[] = [];

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public EnclosureID!: string;

    @validate({ zodValidator: ThirdPartyDeviceTypeString })
    @prop({ type: () => String, required: true })
    public deviceType!: ThirdPartyDeviceType;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastContact?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastPositionChange?: Date;

    /** Raw battery voltage (V) as reported by the device. */
    @validate({
        zodValidator: z.number().optional().describe("Raw battery voltage (V) as reported by the device."),
        responseOnly: true,
    })
    @prop({ type: () => Number, required: false })
    public batteryIndicator?: number;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional() })
    @trackMixed
    @prop({ type: () => Object, required: true, allowMixed: Severity.ALLOW, default: {} })
    public deviceTypeSpecificInformation: Record<string, any> = {};

    static getters = { ...getters } as const;

    static utilities = {
        /**
         * Returns the online status of the device based on lastContact compared to referenceTime.
         * Uses the per-device-type fallback offline threshold from DeviceTypeConfigurations.
         * Note: to be migrated to a warnings-based approach in the future.
         */
        getOnlineStatus: (self: ThirdPartyDevice, referenceTime: Date = TimeOverride.now()): ONLINE_STATUS => {
            if (!self.lastContact) {
                return ONLINE_STATUS.AwaitingConnection;
            }
            const lastContactTime = new Date(self.lastContact).getTime();
            if (Number.isNaN(lastContactTime)) {
                return ONLINE_STATUS.AwaitingConnection;
            }
            const deviceTypeConfiguration = DeviceTypeConfigurations[self.deviceType];
            if (deviceTypeConfiguration === undefined) {
                console.error(
                    `Unable to calculate offline state for third-party device with unconfigured deviceType "${self.deviceType}"`,
                    { node_id: self.node_id, deviceType: self.deviceType },
                );
                return ONLINE_STATUS.LiveMonitoringNotSupported;
            }
            const { fallbackOfflineThresholdMs } = deviceTypeConfiguration;
            if (referenceTime.getTime() - lastContactTime > fallbackOfflineThresholdMs) {
                return ONLINE_STATUS.Offline;
            }
            return ONLINE_STATUS.Online;
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ThirdPartyDeviceSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ThirdPartyDevice, ThirdPartyDeviceFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ThirdPartyDevice.dtoValidators = getZodSchemaFromClass(
    ThirdPartyDevice,
) as unknown as DtoValidatorTypes<ThirdPartyDeviceSchemaTypes>;
